import { z } from 'zod';
import { dongAnhChupSchema, dieuChinhSchema, trangThaiDoiSoatSchema } from './doiSoat.js';
/**
 * Thuế TNCN, quy đổi VND và biên bản chia lợi nhuận (bước 3/3).
 *
 * Hai hàm thuần quan trọng ở đây:
 *
 * - {@link tinhThueTncn} — luật khấu trừ, **không** viết cứng tỷ lệ hay ngưỡng:
 *   cả hai lấy từ cài đặt công ty, vì luật đổi thì kế toán sửa cài đặt chứ
 *   không chờ bản phát hành mới.
 * - {@link docSoTiengViet} — đọc số tiền thành chữ cho biên bản. Biên bản không
 *   có chữ là biên bản ai cũng sửa được một chữ số.
 */
/* ------------------------------------------------------------------ */
/* Loại thuế                                                           */
/* ------------------------------------------------------------------ */
export const LOAI_THUE_TNCN = ['khau_tru_10', 'luy_tien', 'cam_ket_08'];
export const loaiThueTncnSchema = z.enum(LOAI_THUE_TNCN);
export const LOAI_THUE_TNCN_LABEL = {
    khau_tru_10: 'Khấu trừ 10%',
    luy_tien: 'Biểu luỹ tiến (HĐLĐ ≥ 3 tháng)',
    cam_ket_08: 'Cam kết 08 (không khấu trừ)',
};
export const LOAI_THUE_TNCN_MO_TA = {
    khau_tru_10: 'Khấu trừ tại nguồn theo tỷ lệ trong Cài đặt công ty, chỉ khi mỗi lần chi đạt ngưỡng.',
    luy_tien: 'App không tự tính — kế toán tính theo biểu luỹ tiến rồi nhập số thuế.',
    cam_ket_08: 'Có bản cam kết 08 nên không khấu trừ; thuế bằng 0.',
};
export const MAC_DINH_LOAI_THUE = 'khau_tru_10';
/* ------------------------------------------------------------------ */
/* Số học VND                                                          */
/* ------------------------------------------------------------------ */
/**
 * USD → VND, làm tròn về **đồng**.
 *
 * VND không có đơn vị nhỏ hơn đồng, nên mọi số VND trong app là số nguyên. Giữ
 * phần thập phân rồi in ra "1.234.567,89 ₫" là con số không tồn tại trên giấy
 * chuyển khoản.
 */
export function sangVnd(usd, tyGia) {
    return Math.round(usd * tyGia);
}
/* ------------------------------------------------------------------ */
/* Tính thuế                                                          */
/* ------------------------------------------------------------------ */
export const ketQuaThueSchema = z.object({
    /** Số thuế app tính được, VND. `luy_tien` luôn trả 0 kèm `tuTinh = false`. */
    thue: z.number(),
    /** Thu nhập chịu thuế, VND — số mà tỷ lệ nhân vào. */
    thuNhapChiuThue: z.number(),
    /** Tỷ lệ đã dùng, %. `0` khi không khấu trừ. */
    pct: z.number(),
    loaiThue: loaiThueTncnSchema,
    /** `false` = app **không** tự tính được, kế toán phải nhập tay. */
    tuTinh: z.boolean(),
    /** Câu giải thích in trên giao diện và biên bản. */
    ghiChu: z.string(),
});
const DINH_DANG_VND = new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 0 });
/** `1200000 → "1.200.000 ₫"`. */
export function dinhDangVnd(x) {
    return `${DINH_DANG_VND.format(Math.round(x))} ₫`;
}
/**
 * Thuế TNCN của **một lần chi**.
 *
 * - `khau_tru_10`: đạt ngưỡng thì `round(phaiTraVnd × pct)`, **dưới** ngưỡng thì
 *   0. So sánh `>=`: đúng mốc 2.000.000 là **đã** phải khấu trừ.
 * - `luy_tien`: trả 0 và `tuTinh = false`. App không có giảm trừ bản thân, người
 *   phụ thuộc hay thu nhập cộng dồn cả năm từ nguồn khác — đoán ra một con số ở
 *   đây là đưa kế toán đi kê khai sai.
 * - `cam_ket_08`: 0.
 *
 * Số âm (tạm ứng lớn hơn phần chia) thì không khấu trừ: không có thu nhập.
 */
export function tinhThueTncn(dau) {
    const phaiTra = Math.round(dau.phaiTraVnd);
    if (dau.loaiThue === 'cam_ket_08') {
        return {
            thue: 0,
            thuNhapChiuThue: Math.max(0, phaiTra),
            pct: 0,
            loaiThue: dau.loaiThue,
            tuTinh: true,
            ghiChu: 'Có cam kết 08 — không khấu trừ.',
        };
    }
    if (dau.loaiThue === 'luy_tien') {
        return {
            thue: 0,
            thuNhapChiuThue: Math.max(0, phaiTra),
            pct: 0,
            loaiThue: dau.loaiThue,
            tuTinh: false,
            ghiChu: 'Biểu luỹ tiến — kế toán tính và nhập số thuế.',
        };
    }
    if (phaiTra <= 0) {
        return {
            thue: 0,
            thuNhapChiuThue: 0,
            pct: dau.pctMacDinh,
            loaiThue: dau.loaiThue,
            tuTinh: true,
            ghiChu: 'Kỳ này không có thu nhập để khấu trừ.',
        };
    }
    if (phaiTra < Math.round(dau.nguongVnd)) {
        return {
            thue: 0,
            thuNhapChiuThue: phaiTra,
            pct: dau.pctMacDinh,
            loaiThue: dau.loaiThue,
            tuTinh: true,
            ghiChu: `Dưới ngưỡng ${dinhDangVnd(dau.nguongVnd)} mỗi lần chi — không khấu trừ.`,
        };
    }
    /*
     * Làm tròn **một lần** ở cuối: `phaiTra × pct / 100` trên số nguyên rồi mới
     * chia, để 2.000.000 × 10% ra đúng 200.000 chứ không 199.999,99…
     */
    const thue = Math.round((phaiTra * dau.pctMacDinh) / 100);
    return {
        thue,
        thuNhapChiuThue: phaiTra,
        pct: dau.pctMacDinh,
        loaiThue: dau.loaiThue,
        tuTinh: true,
        ghiChu: `Khấu trừ ${dau.pctMacDinh.toLocaleString('vi-VN', { maximumFractionDigits: 2 })}% tại nguồn (đạt ngưỡng ${dinhDangVnd(dau.nguongVnd)}).`,
    };
}
/** Thực chuyển = phải trả − thuế. */
export function thucChuyenVnd(phaiTraVnd, thueVnd) {
    return Math.round(phaiTraVnd) - Math.round(thueVnd);
}
/* ------------------------------------------------------------------ */
/* Đọc số thành chữ                                                    */
/* ------------------------------------------------------------------ */
const CHU_SO = ['không', 'một', 'hai', 'ba', 'bốn', 'năm', 'sáu', 'bảy', 'tám', 'chín'];
/**
 * Hàng của từng khối ba chữ số, từ khối đơn vị đi lên.
 *
 * Trên "tỷ" thì tiếng Việt lặp lại chu kỳ nghìn/triệu: 10¹² là "nghìn tỷ", 10¹⁵
 * là "triệu tỷ". Bảy mức phủ tới 10²⁰, vượt xa `Number.MAX_SAFE_INTEGER`
 * (9,007 triệu tỷ) nên không khối nào còn rơi vào chuỗi rỗng — trước đây bảng
 * chỉ có bốn mức, và `1.234.567.890.123` đọc thành "Một hai trăm ba mươi bốn
 * tỷ…" vì khối nghìn tỷ mất hàng.
 */
const HANG_LON = ['', ' nghìn', ' triệu', ' tỷ', ' nghìn tỷ', ' triệu tỷ', ' tỷ tỷ'];
/**
 * Đọc một khối ba chữ số.
 *
 * `daySau` = khối này đứng **sau** một khối đã đọc, nên hàng trăm trống vẫn phải
 * nói ra để không mất một bậc:
 *
 * - `20.013.473` → "hai mươi triệu **không trăm** mười ba nghìn…". Bỏ "không
 *   trăm" thì "hai mươi triệu mười ba nghìn" đọc lên nghe như thiếu một bậc.
 * - Khối chỉ có hàng đơn vị thì rút thành "lẻ": `1.000.005` → "một triệu **lẻ**
 *   năm", không phải "không trăm lẻ năm" — đó là cách người Việt đọc tiền.
 *
 * Khối **toàn 0** không đọc chút nào (xem {@link docSoTiengViet}).
 */
function docKhoiBaChuSo(so, daySau) {
    const tram = Math.floor(so / 100);
    const chuc = Math.floor((so % 100) / 10);
    const donVi = so % 10;
    const phan = [];
    if (tram > 0)
        phan.push(`${CHU_SO[tram] ?? ''} trăm`);
    else if (daySau && chuc > 0)
        phan.push('không trăm');
    if (chuc > 1) {
        phan.push(`${CHU_SO[chuc] ?? ''} mươi`);
        // "hai mươi mốt", không phải "hai mươi một".
        if (donVi === 1)
            phan.push('mốt');
        else if (donVi === 5)
            phan.push('lăm');
        else if (donVi > 0)
            phan.push(CHU_SO[donVi] ?? '');
    }
    else if (chuc === 1) {
        phan.push('mười');
        if (donVi === 5)
            phan.push('lăm');
        else if (donVi > 0)
            phan.push(CHU_SO[donVi] ?? '');
    }
    else if (donVi > 0) {
        // Thiếu hàng chục: "một trăm lẻ năm", "một triệu lẻ năm".
        if (tram > 0 || daySau)
            phan.push('lẻ');
        phan.push(CHU_SO[donVi] ?? '');
    }
    return phan.join(' ');
}
/**
 * `1200000 → "Một triệu hai trăm nghìn đồng"`.
 *
 * Dùng cho dòng "bằng chữ" của biên bản. Viết hoa chữ đầu, kết bằng "đồng".
 *
 * Quy ước đã chốt:
 * - Số âm đọc kèm "âm" ở đầu (tạm ứng lớn hơn phần chia là việc có thật).
 * - `0` → "Không đồng".
 * - Khối toàn 0 ở giữa bị **bỏ** chứ không đọc "không nghìn": `1.000.005` ra
 *   "Một triệu lẻ năm đồng", đúng cách người Việt đọc tiền.
 * - Phần thập phân bị bỏ (VND không có đơn vị nhỏ hơn đồng).
 */
export function docSoTiengViet(x) {
    const nguyen = Math.abs(Math.round(x));
    if (nguyen === 0)
        return 'Không đồng';
    /* Tách thành các khối ba chữ số, khối đơn vị ở cuối. */
    const khoi = [];
    for (let n = nguyen; n > 0; n = Math.floor(n / 1000))
        khoi.push(n % 1000);
    const phan = [];
    for (let i = khoi.length - 1; i >= 0; i -= 1) {
        const giaTri = khoi[i] ?? 0;
        if (giaTri === 0)
            continue;
        /* Khối đầu tiên được đọc không cần "không trăm" hay "lẻ" dẫn đường. */
        const daySau = phan.length > 0;
        phan.push(`${docKhoiBaChuSo(giaTri, daySau)}${HANG_LON[i] ?? ''}`);
    }
    const chu = `${x < 0 ? 'âm ' : ''}${phan.join(' ')} đồng`.replace(/\s+/g, ' ').trim();
    return chu.charAt(0).toUpperCase() + chu.slice(1);
}
/* ------------------------------------------------------------------ */
/* Cài đặt công ty                                                     */
/* ------------------------------------------------------------------ */
export const caiDatCongTySchema = z.object({
    tenCongTy: z.string(),
    mst: z.string(),
    diaChi: z.string(),
    nguoiDaiDien: z.string(),
    chucVu: z.string(),
    noiLapBienBan: z.string(),
    thueTncnPctMacDinh: z.number(),
    nguongChiMoiLanVnd: z.number(),
    capNhatLuc: z.iso.datetime().nullable(),
    nguoiCapNhat: z.string().nullable(),
});
export const luuCaiDatCongTySchema = z.object({
    tenCongTy: z.string().trim().min(1, 'Phải có tên công ty').max(200),
    mst: z.string().trim().max(20),
    diaChi: z.string().trim().max(400),
    nguoiDaiDien: z.string().trim().max(120),
    chucVu: z.string().trim().max(80),
    noiLapBienBan: z.string().trim().max(120),
    thueTncnPctMacDinh: z.number().min(0).max(100),
    nguongChiMoiLanVnd: z.number().min(0).max(1_000_000_000),
});
/** Phần "Bên A" đã chụp vào ảnh chụp — chỉ các trường biên bản cần in. */
export const benAAnhChupSchema = z.object({
    tenCongTy: z.string(),
    mst: z.string(),
    diaChi: z.string(),
    nguoiDaiDien: z.string(),
    chucVu: z.string(),
    noiLapBienBan: z.string(),
});
/* ------------------------------------------------------------------ */
/* Hồ sơ thuế nhân sự                                                  */
/* ------------------------------------------------------------------ */
export const hoSoThueSchema = z.object({
    userId: z.string(),
    tenTaiKhoan: z.string(),
    emailTaiKhoan: z.string(),
    hoTenDayDu: z.string(),
    cccd: z.string(),
    mstCaNhan: z.string(),
    soTaiKhoan: z.string(),
    nganHang: z.string(),
    loaiThue: loaiThueTncnSchema,
    /**
     * `true` khi các số định danh đã bị **che bớt** trước khi ra khỏi server.
     * Giao diện quản lý luôn nhận bản che; chỉ biên bản in bản đầy đủ.
     */
    daChe: z.boolean(),
    capNhatLuc: z.iso.datetime().nullable(),
    nguoiCapNhat: z.string().nullable(),
});
export const luuHoSoThueSchema = z.object({
    hoTenDayDu: z.string().trim().max(120),
    cccd: z.string().trim().max(20),
    mstCaNhan: z.string().trim().max(20),
    soTaiKhoan: z.string().trim().max(40),
    nganHang: z.string().trim().max(120),
    loaiThue: loaiThueTncnSchema,
});
/** Phần "Bên B" đã chụp vào ảnh chụp. In đầy đủ trên biên bản. */
export const benBAnhChupSchema = z.object({
    hoTenDayDu: z.string(),
    cccd: z.string(),
    mstCaNhan: z.string(),
    soTaiKhoan: z.string(),
    nganHang: z.string(),
    loaiThue: loaiThueTncnSchema,
});
/**
 * Che bớt một dãy số định danh: giữ 4 ký tự cuối.
 *
 * `0123456789 → "••••••6789"`. Giữ **cuối** chứ không giữ đầu: bốn số cuối là
 * phần người ta dùng để đối chiếu, còn phần đầu của CCCD là mã tỉnh và năm sinh.
 */
export function cheDaySo(so) {
    const sach = so.trim();
    if (sach.length <= 4)
        return sach === '' ? '' : '•'.repeat(sach.length);
    return `${'•'.repeat(sach.length - 4)}${sach.slice(-4)}`;
}
/* ------------------------------------------------------------------ */
/* Biên bản                                                            */
/* ------------------------------------------------------------------ */
export const bienBanSchema = z.object({
    payoutId: z.string(),
    period: z.string(),
    phienBan: z.number().int().positive(),
    /** `false` = phiên bản đã bị thay; biên bản in kèm dấu "ĐÃ THAY THẾ". */
    hieuLuc: z.boolean(),
    /** Nhãn in trên biên bản, ví dụ `Phiên bản 2 — chốt lại ngày 12/11/2026`. */
    nhanPhienBan: z.string(),
    /** Số phiên bản đang hiệu lực, để biên bản cũ trỏ sang. `null` khi không có. */
    phienBanThayThe: z.number().int().positive().nullable(),
    trangThai: trangThaiDoiSoatSchema,
    benA: benAAnhChupSchema,
    benB: benBAnhChupSchema,
    tenNguoi: z.string(),
    emailNguoi: z.string(),
    kyTu: z.string(),
    kyDen: z.string(),
    dong: z.array(dongAnhChupSchema),
    khoanDieuChinh: z.array(dieuChinhSchema),
    tongPhanChia: z.number(),
    tongDieuChinh: z.number(),
    phaiTra: z.number(),
    /** `null` khi chưa nhập tỷ giá cho phiên bản này. */
    tyGiaVnd: z.number().nullable(),
    phaiTraVnd: z.number().nullable(),
    thuNhapChiuThueVnd: z.number().nullable(),
    thueTncn: z.number().nullable(),
    thueTncnPct: z.number().nullable(),
    loaiThue: loaiThueTncnSchema.nullable(),
    thueGhiDe: z.boolean(),
    thueGhiDeLyDo: z.string().nullable(),
    thucChuyenVnd: z.number().nullable(),
    /** Dòng "bằng chữ" của số thực chuyển. `null` khi chưa có tỷ giá. */
    thucChuyenBangChu: z.string().nullable(),
    chotAt: z.iso.datetime(),
    nguoiChot: z.string().nullable(),
    thanhToanNgay: z.string().nullable(),
    thanhToanGhiChu: z.string().nullable(),
    /** Ngày lập biên bản = ngày thanh toán, chưa trả thì là ngày chốt. */
    ngayLap: z.string(),
});
/* ------------------------------------------------------------------ */
/* Input quy đổi / thanh toán                                          */
/* ------------------------------------------------------------------ */
export const luuQuyDoiSchema = z
    .object({
    /** USD→VND. Thiếu thì giữ tỷ giá đang có của phiên bản. */
    tyGiaVnd: z.number().positive().max(1_000_000).optional(),
    /**
     * Số thuế **ghi đè** (VND). Bắt buộc với `luy_tien`; với loại khác thì chỉ
     * nhập khi quản trị muốn khác số app tính, và phải kèm lý do.
     */
    thueTncn: z.number().min(0).max(1_000_000_000).optional(),
    thueGhiDeLyDo: z.string().trim().max(500).optional(),
})
    .refine((x) => x.tyGiaVnd !== undefined || x.thueTncn !== undefined, {
    message: 'Nhập tỷ giá hoặc số thuế',
    path: ['tyGiaVnd'],
});
/* ------------------------------------------------------------------ */
/* Thu nhập của tôi                                                    */
/* ------------------------------------------------------------------ */
export const kyThuNhapSchema = z.object({
    period: z.string(),
    payoutId: z.string().nullable(),
    phienBan: z.number().int().positive().nullable(),
    trangThai: trangThaiDoiSoatSchema,
    /**
     * `false` khi kỳ còn **nháp**: mọi số trả `null`. Số chưa chốt không phải lời
     * hứa trả tiền, nên không hiện cho người nhận.
     */
    coSo: z.boolean(),
    soKenh: z.number().int().nonnegative().nullable(),
    phanChia: z.number().nullable(),
    dieuChinh: z.number().nullable(),
    phaiTra: z.number().nullable(),
    phaiTraVnd: z.number().nullable(),
    thueTncn: z.number().nullable(),
    thucChuyenVnd: z.number().nullable(),
    loaiThue: loaiThueTncnSchema.nullable(),
    thanhToanNgay: z.string().nullable(),
    khoanDieuChinh: z.array(dieuChinhSchema),
});
export const thuNhapCuaToiSchema = z.object({
    userId: z.string(),
    tenNguoi: z.string(),
    /** Hồ sơ thuế của chính mình, đã che bớt. `null` khi chưa khai. */
    hoSo: hoSoThueSchema.nullable(),
    ky: z.array(kyThuNhapSchema),
    tongDaTraVnd: z.number(),
    tongThueVnd: z.number(),
});
/* ------------------------------------------------------------------ */
/* Báo cáo thuế                                                        */
/* ------------------------------------------------------------------ */
export const PHAM_VI_BAO_CAO_THUE = ['thang', 'quy', 'nam'];
export const PHAM_VI_BAO_CAO_THUE_LABEL = {
    thang: 'Theo tháng',
    quy: 'Theo quý',
    nam: 'Theo năm',
};
export const baoCaoThueQuerySchema = z.object({
    phamVi: z.enum(PHAM_VI_BAO_CAO_THUE).default('thang'),
    /** `YYYY-MM` với tháng, `YYYY-Qn` với quý, `YYYY` với năm. */
    moc: z.string().regex(/^\d{4}(-(0[1-9]|1[0-2]|Q[1-4]))?$/, 'Mốc kỳ không hợp lệ'),
});
/** Các kỳ tháng mà một mốc báo cáo trỏ tới. */
export function thangCuaMoc(phamVi, moc) {
    if (phamVi === 'thang')
        return [moc];
    if (phamVi === 'quy') {
        const [nam, q] = moc.split('-Q');
        const soQ = Number(q);
        if (!nam || !Number.isInteger(soQ) || soQ < 1 || soQ > 4)
            return [];
        return [0, 1, 2].map((i) => `${nam}-${String((soQ - 1) * 3 + 1 + i).padStart(2, '0')}`);
    }
    const nam = moc.slice(0, 4);
    return Array.from({ length: 12 }, (_, i) => `${nam}-${String(i + 1).padStart(2, '0')}`);
}
export function nhanMocBaoCao(phamVi, moc) {
    if (phamVi === 'nam')
        return `Năm ${moc.slice(0, 4)}`;
    if (phamVi === 'quy')
        return `Quý ${moc.replace('-Q', '/')}`;
    const [nam, thang] = moc.split('-');
    return `Tháng ${thang ?? '?'}/${nam ?? '?'}`;
}
//# sourceMappingURL=thue.js.map